import { useCallback, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { email, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';

import { checkForcedSsoLogin } from 'bundles/third-party-auth/api';
import { type CheckForcedSsoLoginResponse } from 'bundles/third-party-auth/types/responseTypes';

export const INPUT_DEBOUNCE_TIME = 300;

export type EnterpriseSsoLoginInfo = {
  isEnterpriseSsoLogin: boolean;
  redirectUrl: string;
  isLoading: boolean;
  isRedirecting: boolean;
};

const enterpriseSsoLoginInfoInitialState: EnterpriseSsoLoginInfo = {
  isEnterpriseSsoLogin: false,
  redirectUrl: '',
  isLoading: false,
  isRedirecting: false,
};

type UseEnterpriseSsoEmailCheckOptions = {
  onEnforcedSSO?: () => void;
  preferredOrgIds?: string[];
  returnTo?: string;
};

type UseEnterpriseSsoEmailCheckReturn = {
  enterpriseSsoLoginInfo: EnterpriseSsoLoginInfo;
  onEmailChange: (email: string) => void;
  checkForceSSO: (email: string) => Promise<CheckForcedSsoLoginResponse | null>;
  onContinueWithSso: () => void;
  resetEnterpriseSsoInfo: () => void;
};

const useEnterpriseSsoEmailCheck = (
  options: UseEnterpriseSsoEmailCheckOptions = {}
): UseEnterpriseSsoEmailCheckReturn => {
  const [enterpriseSsoLoginInfo, setEnterpriseSsoLoginInfo] = useState<EnterpriseSsoLoginInfo>(
    enterpriseSsoLoginInfoInitialState
  );

  // Callers commonly pass a fresh `options` object on every render. Storing it in a ref
  // lets the callbacks below keep stable identities so `debouncedCheckForceSSO` isn't
  // recreated (and its pending timers discarded) on each render — which previously caused
  // `/enforceSso` to be called in a tight loop.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const resetEnterpriseSsoInfo = useCallback<VoidFunction>(() => {
    setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
  }, []);

  const setIsRedirecting = useCallback<(x: boolean) => void>((isRedirecting: boolean) => {
    setEnterpriseSsoLoginInfo((state) => ({ ...state, isRedirecting }));
  }, []);

  const checkForceSSO = useCallback(async (emailField: string): Promise<CheckForcedSsoLoginResponse | null> => {
    const errors = runValidations(emailField, [required, email]);
    if (errors) {
      setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
      return null;
    }

    setEnterpriseSsoLoginInfo((state) => ({ ...state, isLoading: true }));
    const currentOptions = optionsRef.current;

    try {
      const response: CheckForcedSsoLoginResponse = await checkForcedSsoLogin(
        emailField,
        currentOptions.returnTo,
        currentOptions
      );
      if (response.isForceSSOLogin) {
        setEnterpriseSsoLoginInfo((state) => ({
          ...state,
          isEnterpriseSsoLogin: true,
          isLoading: false,
          redirectUrl: response.redirectUrl,
        }));
      } else {
        setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
      }
      return response;
    } catch {
      setEnterpriseSsoLoginInfo(enterpriseSsoLoginInfoInitialState);
      return null;
    }
    // Deps intentionally empty: `options` is read via optionsRef.current
    // (see comment on optionsRef above) so this callback's identity stays
    // stable across renders and the debounced wrapper isn't recreated.
  }, []);

  const debouncedCheckForceSSO = useMemo(() => debounce(checkForceSSO, INPUT_DEBOUNCE_TIME), [checkForceSSO]);

  const onEmailChange = useCallback<(email: string) => void>(
    (emailInput) => {
      debouncedCheckForceSSO(emailInput);
      return () => debouncedCheckForceSSO.cancel();
    },
    [debouncedCheckForceSSO]
  );

  const redirectUrl = enterpriseSsoLoginInfo.redirectUrl;
  const onContinueWithSso = useCallback<VoidFunction>(() => {
    setIsRedirecting(true);
    window.location.replace(redirectUrl);
  }, [redirectUrl, setIsRedirecting]);

  return {
    enterpriseSsoLoginInfo,
    onEmailChange,
    checkForceSSO,
    onContinueWithSso,
    resetEnterpriseSsoInfo,
  };
};

export default useEnterpriseSsoEmailCheck;
